import type { CurrentUserRead } from "@/types/user/user";
import { useTranslation } from "react-i18next";
import { useSessionReauthentication } from "./useSessionReauthentication";

interface Props {
  user: CurrentUserRead;
  onRecovered(user: CurrentUserRead, access: string): void;
  onSignOut(): Promise<void>;
}
export function SessionRecoveryPanel({ user, onRecovered, onSignOut }: Props) {
  const { t } = useTranslation();
  const recovery = useSessionReauthentication(user, onRecovered);
  return (
    <section
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-100 p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-recovery-title"
    >
      <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-lg">
        <h1 id="session-recovery-title" className="text-xl font-semibold">
          {t("session_recovery.title")}
        </h1>
        <p>{t("session_recovery.description")}</p>
        <p>{t("session_recovery.owner", { username: user.username })}</p>
        {recovery.failed && <p role="alert">{t("session_recovery.failed")}</p>}
        {recovery.differentUser && (
          <p role="alert">{t("session_recovery.different_user")}</p>
        )}
        <button
          type="button"
          disabled={recovery.busy}
          onClick={() => void recovery.retry()}
          className="rounded border px-4 py-2"
        >
          {t("session_recovery.retry")}
        </button>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void recovery.login();
          }}
        >
          {recovery.tempToken ? (
            <>
              <label className="block">
                {t("session_recovery.method")}
                <select
                  value={recovery.method}
                  disabled={recovery.busy}
                  onChange={(event) =>
                    recovery.setMethod(
                      event.target.value === "backup" ? "backup" : "totp",
                    )
                  }
                >
                  <option value="totp">{t("session_recovery.totp")}</option>
                  <option value="backup">{t("session_recovery.backup")}</option>
                </select>
              </label>
              <label className="block">
                {t("session_recovery.code")}
                <input
                  className="block w-full rounded border p-2"
                  required
                  autoComplete="one-time-code"
                  value={recovery.code}
                  disabled={recovery.busy}
                  onChange={(event) => recovery.setCode(event.target.value)}
                />
              </label>
            </>
          ) : (
            <label className="block">
              {t("session_recovery.password")}
              <input
                className="block w-full rounded border p-2"
                type="password"
                required
                autoComplete="current-password"
                value={recovery.password}
                disabled={recovery.busy}
                onChange={(event) => recovery.setPassword(event.target.value)}
              />
            </label>
          )}
          <button
            className="rounded bg-slate-900 px-4 py-2 text-white"
            disabled={recovery.busy}
            type="submit"
          >
            {t("session_recovery.login")}
          </button>
        </form>
        <button
          className="text-sm underline"
          disabled={recovery.busy}
          onClick={() => void onSignOut()}
        >
          {t("session_recovery.signout")}
        </button>
      </div>
    </section>
  );
}
